/**
 * A frota como o BANCO a devolve — e a traducao para o que a tela lê.
 *
 * Este arquivo existe ao lado de `tipos.ts`, e nao no lugar dele, por um motivo
 * de fronteira: `Equipamento`, de `tipos.ts`, e a forma da FIXTURE, e o modulo
 * de combustivel ainda depende dela (ele precisa de `horimetro` e `custoHora`,
 * que sao do modulo 03 e do 11 e nao existem no modelo do modulo 02). Trocar a
 * forma daquele tipo quebraria a tela de lancamento de outro modulo, que nao e
 * deste territorio. Esta registrado em `docs/PENDENCIAS.md`.
 *
 * ## O que a heranca faz aqui
 *
 * `origemDoMedidor` e `origemDaCapacidade` viajam do banco ate a tela porque
 * "18.000 L" e "18.000 L, herdado da configuracao" sao respostas diferentes
 * para quem esta conferindo por que uma maquina consome fora da faixa. A view
 * `ativos.equipamento_efetivo` resolve a heranca uma vez; a tela mostra o valor
 * E de onde ele veio, em vez de esconder a delegacao.
 */
import { ehStatusEquipamento, type StatusEquipamento } from '@/lib/dominio';
import { formatarFaixa, unidadeDeConsumo } from '@/lib/formato';

import { paraInstante, paraNumero, paraTexto } from './api';

/**
 * De onde veio um valor herdavel.
 *
 * `modelo` existe por causa do `tipo` (0124): ele e o unico campo que herda do
 * MODELO, e nao da configuracao — a configuracao nunca teve tipo. Dizer
 * `configuracao` nele mandaria a tela oferecer "herdado da configuração" e a
 * pessoa procurar o valor onde ele nao esta.
 */
export type OrigemDoValor = 'unidade' | 'configuracao' | 'modelo' | 'padrao' | 'ausente';

export type TipoDeMedidor = 'horimetro' | 'odometro';

/** O que o motor queima (0060). A mesma lista do `check` do banco. */
export type Combustivel = 'diesel' | 'gasolina' | 'etanol' | 'flex' | 'gnv' | 'eletrico';

/** Os combustiveis na ordem da tela, com a grafia de cada um. */
export const COMBUSTIVEIS: readonly { readonly valor: Combustivel; readonly rotulo: string }[] = [
  { valor: 'diesel', rotulo: 'Diesel' },
  { valor: 'gasolina', rotulo: 'Gasolina' },
  { valor: 'etanol', rotulo: 'Etanol' },
  { valor: 'flex', rotulo: 'Flex' },
  { valor: 'gnv', rotulo: 'GNV' },
  { valor: 'eletrico', rotulo: 'Elétrico' },
];

/** "Diesel", "GNV"; `null` e o que a tela escreve como "Não informado". */
export function rotuloDoCombustivel(valor: Combustivel | null): string | null {
  return COMBUSTIVEIS.find((c) => c.valor === valor)?.rotulo ?? null;
}

function lerCombustivel(valor: unknown): Combustivel | null {
  return COMBUSTIVEIS.find((c) => c.valor === valor)?.valor ?? null;
}

/** Uma maquina da frota, com alocacao e situacao CORRENTES ja resolvidas. */
export interface EquipamentoDaFrota {
  readonly id: string;
  /** Identidade da linha. Vai em monoespacada e nao some ao rolar (p.18). */
  readonly codigo: string;
  readonly descricao: string;
  readonly numeroSerie: string | null;
  readonly anoFabricacao: number | null;
  readonly ativo: boolean;

  /* 0060 — o que a planilha de cadastro da empresa trazia e a ficha nao tinha. */
  /** O numero que o controle patrimonial da empresa da a maquina. */
  readonly patrimonio: string | null;
  /** O codigo do dia a dia da empresa, "CB-07" (0125). Nao e o patrimonio. */
  readonly codigoInterno: string | null;
  readonly combustivel: Combustivel | null;
  /**
   * 0133 (decisao 59): a maquina abastece fora dos tanques da empresa, e por
   * isso aparece na lista do posto parceiro. Quem marca e quem administra a
   * frota; nasce desligada.
   */
  readonly abasteceEmPosto: boolean;
  /** O ano do modelo: o de fabricacao ou o seguinte. */
  readonly anoModelo: number | null;
  readonly anoAquisicao: number | null;
  /** Sem hifen, em caixa alta: ABC1234 ou, no padrao Mercosul, ABC1D23. */
  readonly placa: string | null;
  /** Os 11 digitos, com o zero a esquerda. */
  readonly renavam: string | null;

  /** A hierarquia: fabricante > modelo > configuracao > unidade. */
  readonly fabricante: string | null;
  readonly modelo: string | null;
  /**
   * "Escavadeira hidraulica", "Motoniveladora", "Caminhao pipa".
   *
   * Herda do MODELO e a unidade sobrepoe (0124): o mesmo chassi carrega
   * cacamba ou tanque, e o implemento troca na vida da maquina.
   */
  readonly tipo: string | null;
  readonly origemDoTipo: OrigemDoValor;
  readonly configuracao: string | null;
  readonly configuracaoId: string | null;

  readonly tipoMedidor: TipoDeMedidor;
  readonly origemDoMedidor: OrigemDoValor;
  readonly capacidadeTanqueL: number | null;
  readonly origemDaCapacidade: OrigemDoValor;
  readonly consumoMinimo: number | null;
  readonly consumoMaximo: number | null;
  /** Decisao 8: a faixa e da unidade quando preenchida nela; senao, herdada. */
  readonly origemDaFaixa: OrigemDoValor;

  /**
   * O MOTOR: modelo (herdavel da configuracao) e numero de serie (sempre da
   * unidade). O "como funciona" da ferramenta 1 do modulo 02 pede "fabricante,
   * modelo, configuracao, numero de serie, MOTOR e componentes", e e do motor
   * que dependem garantia e peca compativel. Componentes serializados seguem
   * fora: estao na "Evolucao prevista" do proprio modulo.
   */
  readonly motorModelo: string | null;
  readonly origemDoMotor: OrigemDoValor;
  readonly motorNumeroSerie: string | null;

  readonly filial: string | null;
  readonly filialId: string | null;

  /**
   * `null` quando a maquina nunca teve situacao registrada. NAO e um sétimo
   * status: e a ausencia de registro, e a tela escreve isso com todas as
   * letras em vez de inventar "Disponível".
   */
  readonly status: StatusEquipamento | null;
  readonly motivoDoStatus: string | null;
  readonly statusDesde: Date | null;
  /**
   * O id da situacao vigente (nulo sem situacao). A folha de mudar a situacao
   * o devolve no envio, e o servidor recusa se outra pessoa mudou a situacao
   * depois que a folha abriu (robos de 20/10/2026, achado 10).
   */
  readonly situacaoId: string | null;

  /** Alocacao corrente. `obra` nulo significa que a maquina nao esta alocada. */
  readonly alocacaoId: string | null;
  readonly obra: string | null;
  readonly cliente: string | null;
  readonly centroCusto: string | null;
  readonly contrato: string | null;
  readonly alocadoDesde: Date | null;

  readonly criadoEm: Date | null;
  readonly atualizadoEm: Date | null;
  /**
   * A versao da ficha (`backend/src/http/versao-da-ficha.ts`): o formulario de
   * editar a guarda ao abrir e a devolve no envio, e o servidor recusa se
   * outra pessoa gravou depois (robos de 20/10/2026, achado 1).
   */
  readonly versao: string | null;

  /**
   * A tarefa preventiva mais urgente da maquina (decisao 16 do dono). Tres
   * estados, e a tela escreve cada um de um jeito: sem a permissao do modulo
   * 05, com permissao e sem plano que alcance a maquina, e a tarefa.
   */
  readonly proximaManutencao: Secao<{ readonly item: ProximaManutencao | null }>;

  /**
   * A foto atual da maquina (ADR-007), ou `null`. A lista so traz o que a
   * tabela desenha — a miniatura de 32 px —; a foto inteira e pedida pela
   * ficha. Chave AUSENTE (prontuario, cadastro, ajuste da ficha) vira `null`
   * tambem: essas respostas nao falam de foto.
   */
  readonly foto: FotoNaLista | null;

  /**
   * A ultima leitura do medidor que a maquina tem — o cartao da frota no
   * celular escreve "BR-163 Lote 2 · 4.832 h" (M07). Chave ausente (respostas
   * que nao sao a lista) vira `null`.
   */
  readonly medidorAtual: { readonly valor: number; readonly lidaEm: Date | null } | null;
}

/** A foto como a LISTA a ve: o bastante para a miniatura da tabela. */
export interface FotoNaLista {
  readonly id: string;
  /**
   * URL assinada da miniatura, igual dentro da mesma janela de 5 minutos para
   * o navegador reaproveitar do cache. `null` enquanto a miniatura nao subiu,
   * ou com o armazenamento desligado: a tabela mostra o icone do tipo.
   */
  readonly miniaturaUrl: string | null;
  readonly enviadoEm: Date | null;
}

/**
 * A foto como a FICHA a ve. A chave do armazenamento e o SHA-256 nunca vem
 * para a tela; `enviadoPor` e o NOME de quem enviou.
 */
export interface FotoDoEquipamento {
  readonly id: string;
  readonly equipamentoId: string;
  readonly mime: string;
  readonly tamanhoBytes: number;
  readonly largura: number;
  readonly altura: number;
  readonly temMiniatura: boolean;
  readonly enviadoEm: Date | null;
  readonly enviadoPor: string | null;
}

function comoObjeto(valor: unknown): Record<string, unknown> | null {
  return valor !== null && typeof valor === 'object' && !Array.isArray(valor)
    ? (valor as Record<string, unknown>)
    : null;
}

/** `foto` de uma linha de `GET /equipamentos`. Sem `id`, nao ha foto. */
export function lerFotoNaLista(valor: unknown): FotoNaLista | null {
  const f = comoObjeto(valor);
  const id = paraTexto(f?.['id']);
  if (!f || !id) return null;
  return {
    id,
    miniaturaUrl: paraTexto(f['miniaturaUrl']),
    enviadoEm: paraInstante(f['enviadoEm']),
  };
}

/** `foto` das respostas de `/equipamentos/:id/foto`. Sem `id`, nao ha foto. */
export function lerFoto(valor: unknown): FotoDoEquipamento | null {
  const f = comoObjeto(valor);
  const id = paraTexto(f?.['id']);
  if (!f || !id) return null;
  return {
    id,
    equipamentoId: String(f['equipamentoId'] ?? ''),
    mime: paraTexto(f['mime']) ?? 'image/jpeg',
    tamanhoBytes: paraNumero(f['tamanhoBytes']) ?? 0,
    largura: paraNumero(f['largura']) ?? 0,
    altura: paraNumero(f['altura']) ?? 0,
    temMiniatura: f['temMiniatura'] === true,
    enviadoEm: paraInstante(f['enviadoEm']),
    enviadoPor: paraTexto(f['enviadoPor']),
  };
}

export interface ProximaManutencao {
  readonly descricao: string;
  readonly estado: string;
  readonly gatilho: string;
  readonly faltam: number | null;
  readonly previstaPara: string | null;
}

function lerProxima(linha: Record<string, unknown>): Secao<{ readonly item: ProximaManutencao | null }> {
  /* A chave AUSENTE quer dizer que o servidor nao mandou por falta de
     permissao; presente e nula quer dizer que nenhum plano alcanca a maquina. */
  if (!Object.hasOwn(linha, 'proxima_manutencao')) return { permitido: false };
  const p = linha['proxima_manutencao'] as Record<string, unknown> | null;
  if (!p) return { permitido: true, item: null };
  return {
    permitido: true,
    item: {
      descricao: String(p['descricao'] ?? ''),
      estado: String(p['estado'] ?? ''),
      gatilho: String(p['gatilho'] ?? ''),
      faltam: paraNumero(p['faltam']),
      previstaPara: paraTexto(p['previstaPara']),
    },
  };
}

function origem(valor: unknown): OrigemDoValor {
  return valor === 'unidade' || valor === 'configuracao' || valor === 'modelo' || valor === 'padrao' || valor === 'ausente'
    ? valor
    : 'ausente';
}

/**
 * A traducao de linha do banco para objeto de tela.
 *
 * O `status` passa por `ehStatusEquipamento` e nao por um `as`: o banco ja tem
 * `check` com os seis nomes oficiais, mas uma conversao cega aqui faria um
 * sétimo status — vindo de um banco antigo, de um dado semeado a mao — chegar
 * na tela como se fosse legitimo, e o badge nao teria cor para ele. Preferimos
 * `null`, que a tela sabe escrever.
 */
export function lerEquipamento(linha: Record<string, unknown>): EquipamentoDaFrota {
  const status = linha['status'];
  const medidor = linha['tipo_medidor'];

  return {
    id: String(linha['id'] ?? ''),
    codigo: String(linha['codigo'] ?? ''),
    descricao: String(linha['descricao'] ?? ''),
    numeroSerie: paraTexto(linha['numero_serie']),
    anoFabricacao: paraNumero(linha['ano_fabricacao']),
    ativo: linha['ativo'] !== false,

    patrimonio: paraTexto(linha['patrimonio']),
    codigoInterno: paraTexto(linha['codigo_interno']),
    combustivel: lerCombustivel(linha['combustivel']),
    abasteceEmPosto: linha['abastece_em_posto'] === true,
    anoModelo: paraNumero(linha['ano_modelo']),
    anoAquisicao: paraNumero(linha['ano_aquisicao']),
    placa: paraTexto(linha['placa']),
    renavam: paraTexto(linha['renavam']),

    fabricante: paraTexto(linha['fabricante']),
    modelo: paraTexto(linha['modelo']),
    tipo: paraTexto(linha['tipo']),
    origemDoTipo: origem(linha['origem_do_tipo']),
    configuracao: paraTexto(linha['configuracao']),
    configuracaoId: paraTexto(linha['configuracao_id']),

    tipoMedidor: medidor === 'odometro' ? 'odometro' : 'horimetro',
    origemDoMedidor: origem(linha['origem_do_medidor']),
    capacidadeTanqueL: paraNumero(linha['capacidade_tanque_l']),
    origemDaCapacidade: origem(linha['origem_da_capacidade']),
    consumoMinimo: paraNumero(linha['consumo_minimo']),
    consumoMaximo: paraNumero(linha['consumo_maximo']),
    origemDaFaixa: origem(linha['origem_da_faixa']),

    motorModelo: paraTexto(linha['motor_modelo']),
    origemDoMotor: origem(linha['origem_do_motor']),
    motorNumeroSerie: paraTexto(linha['motor_numero_serie']),

    filial: paraTexto(linha['filial']),
    filialId: paraTexto(linha['filial_id']),

    status: ehStatusEquipamento(status) ? status : null,
    motivoDoStatus: paraTexto(linha['motivo_do_status']),
    statusDesde: paraInstante(linha['status_desde']),
    situacaoId: paraTexto(linha['situacao_id']),

    alocacaoId: paraTexto(linha['alocacao_id']),
    obra: paraTexto(linha['obra']),
    cliente: paraTexto(linha['cliente']),
    centroCusto: paraTexto(linha['centro_custo']),
    contrato: paraTexto(linha['contrato']),
    alocadoDesde: paraInstante(linha['alocado_desde']),

    criadoEm: paraInstante(linha['criado_em']),
    atualizadoEm: paraInstante(linha['atualizado_em']),
    versao: paraTexto(linha['versao']),
    proximaManutencao: lerProxima(linha),
    foto: lerFotoNaLista(linha['foto']),
    medidorAtual: lerMedidorAtual(linha['medidor_atual']),
  };
}

function lerMedidorAtual(valor: unknown): EquipamentoDaFrota['medidorAtual'] {
  if (valor === null || typeof valor !== 'object') return null;
  const m = valor as Record<string, unknown>;
  const leitura = paraNumero(m['valor']);
  return leitura === null ? null : { valor: leitura, lidaEm: paraInstante(m['lidaEm']) };
}

/**
 * A faixa do manual como o prontuario a escreve: `0,35 a 0,55 L/km`.
 *
 * As pontas sao as do catalogo (`formatarFaixa`, com as casas que tem) e a
 * unidade e a do medidor. Com `formatarDecimal(v, 1)` o prontuario arredondava
 * 0,35 para 0,4, e o catalogo e o prontuario da mesma maquina diziam faixas
 * diferentes.
 */
export function textoDaFaixaDoManual(
  equipamento: Pick<EquipamentoDaFrota, 'consumoMinimo' | 'consumoMaximo' | 'tipoMedidor'>,
): string {
  const faixa = formatarFaixa(equipamento.consumoMinimo, equipamento.consumoMaximo);
  return faixa === null ? 'Não informado' : `${faixa} ${unidadeDeConsumo(equipamento.tipoMedidor)}`;
}

/** Uma vigencia de alocacao. Historico, nunca so o valor atual. */
export interface Alocacao {
  readonly id: string;
  readonly inicio: Date | null;
  /** `null` e a vigencia ABERTA: a linha corrente. */
  readonly fim: Date | null;
  readonly obra: string | null;
  readonly cliente: string | null;
  readonly centroCusto: string | null;
  readonly contrato: string | null;
  readonly motivo: string | null;
}

export function lerAlocacao(linha: Record<string, unknown>): Alocacao {
  return {
    id: String(linha['id'] ?? ''),
    inicio: paraInstante(linha['inicio']),
    fim: paraInstante(linha['fim']),
    obra: paraTexto(linha['obra']),
    cliente: paraTexto(linha['cliente']),
    centroCusto: paraTexto(linha['centro_custo']),
    contrato: paraTexto(linha['contrato']),
    motivo: paraTexto(linha['motivo']),
  };
}

/** Uma vigencia de situacao. O motivo e obrigatorio no banco, entao nunca e nulo. */
export interface Situacao {
  readonly id: string;
  readonly status: StatusEquipamento | null;
  readonly motivo: string;
  readonly inicio: Date | null;
  readonly fim: Date | null;
}

export function lerSituacao(linha: Record<string, unknown>): Situacao {
  const status = linha['status'];
  return {
    id: String(linha['id'] ?? ''),
    status: ehStatusEquipamento(status) ? status : null,
    motivo: String(linha['motivo'] ?? ''),
    inicio: paraInstante(linha['inicio']),
    fim: paraInstante(linha['fim']),
  };
}

/** Uma linha da trilha de auditoria, que e a linha do tempo do ativo. */
export interface EventoDoProntuario {
  readonly ocorridoEm: Date | null;
  readonly acao: string;
  readonly recurso: string;
  readonly resultado: string;
  readonly motivo: string | null;
  readonly autor: string | null;
}

export function lerEvento(linha: Record<string, unknown>): EventoDoProntuario {
  return {
    ocorridoEm: paraInstante(linha['ocorrido_em']),
    acao: String(linha['acao'] ?? ''),
    recurso: String(linha['recurso'] ?? ''),
    resultado: String(linha['resultado'] ?? ''),
    motivo: paraTexto(linha['motivo']),
    autor: paraTexto(linha['autor']),
  };
}

export interface Prontuario {
  readonly equipamento: EquipamentoDaFrota;
  readonly alocacoes: readonly Alocacao[];
  readonly situacoes: readonly Situacao[];
  readonly historico: readonly EventoDoProntuario[];
  readonly horimetro: LeituraDoHorimetro | null;
  /** As últimas leituras do medidor, cada uma com a origem (0103: a telemetria é uma delas). */
  readonly leituras?: readonly LeituraDoHorimetro[];
  readonly horas: Secao<HorasDoProntuario>;
  readonly abastecimentos: Secao<AbastecimentosDoProntuario>;
  readonly documentos: Secao<{ readonly itens: readonly DocumentoDoProntuario[] }>;
  readonly manutencao: Secao<{ readonly itens: readonly VencimentoDoProntuario[] }>;
  readonly ordens: Secao<OrdensDoProntuario>;
  /** O custo direto dos ultimos 30 dias, pela conta da tela de custos (modulo 11). */
  readonly custo: Secao<CustoDoProntuario>;
  /**
   * O custo do MES FECHADO, com a margem parcial (R06, p.71). Fechado em
   * servidor anterior a R06.
   */
  readonly custoDoMes: Secao<CustoDoMes>;
}

export interface CustoDoMes {
  /** `aaaa-mm-01`: a competencia da medicao. */
  readonly competencia: string;
  readonly de: string;
  readonly ate: string;
  readonly denominador: string;
  readonly valores: {
    readonly combustivel: number;
    readonly estoque: number;
    readonly terceiros: number;
    readonly outros: number;
    /** A mão de obra pelo valor-hora da função (0095); hoje é o que "Outros" soma. */
    readonly maoDeObra: number;
    readonly total: number;
    readonly semPreco: number;
    /** A frase do servidor sobre a hora de pessoa sem valor no mês; nula quando não há. */
    readonly maoDeObraSemValor: string | null;
    readonly custoPorHora: number | null;
    /* 0096 · ao lado do direto; servidor anterior: zero e o total = direto. */
    readonly depreciacao: number;
    /** Sem dado de depreciação no mês: a depreciação está FORA, e não zero. */
    readonly semDepreciacao: boolean;
    readonly indiretoRateado: number;
    /** Direto + depreciação + indireto: o custo que a margem usa. */
    readonly custoTotal: number;
    readonly custoTotalPorHora: number | null;
  } | null;
  /** A receita das medicoes aprovadas da maquina no mes; nula sem base. */
  readonly receita: number | null;
  /** (receita - custo total) / receita; nula sem receita. */
  readonly margem: number | null;
  /** Por que nao ha margem, escrito pelo servidor. */
  readonly semMargemPorque: string | null;
  /** 0096 · o que ficou fora do custo desta máquina no mês, pelas frases do servidor. */
  readonly ausencias: readonly string[];
  /** Verdadeiro só com `ausencias` vazia: é aí que a margem deixa de ser parcial. */
  readonly completo: boolean;
}

/** As O.S. da maquina, a parada programada e as pecas que ela recebeu. */
export interface OrdensDoProntuario {
  readonly itens: readonly OrdemDoProntuario[];
  /** A janela combinada com a operacao (0089), das O.S. abertas. Vazia em servidor anterior. */
  readonly paradasProgramadas: readonly ParadaDoProntuario[];
  /** A peca APLICADA nas O.S. da maquina, a mais recente primeiro. */
  readonly pecasAplicadas: readonly PecaDoProntuario[];
}

export interface ParadaDoProntuario {
  readonly ordemId: string;
  readonly numero: number;
  readonly de: Date | null;
  readonly ate: Date | null;
  readonly combinadaCom: string | null;
  readonly combinadaPor: string | null;
}

export interface PecaDoProntuario {
  readonly id: string;
  readonly ordemId: string;
  readonly numero: number;
  readonly descricao: string;
  readonly quantidade: number;
  readonly unidade: string;
  readonly aplicadaEm: Date | null;
}

export interface CustoDoProntuario {
  /** `aaaa-mm-dd`, o periodo da conta. */
  readonly de: string;
  readonly ate: string;
  readonly denominador: string;
  /** `null`: a maquina nao esta ativa, e a conta da frota so soma as ativas. */
  readonly valores: {
    readonly combustivel: number;
    readonly estoque: number;
    readonly terceiros: number;
    readonly total: number;
    /** Quantas parcelas ficaram sem preco: o total e PARCIAL quando ha alguma. */
    readonly semPreco: number;
    readonly horasMaoDeObra: number;
    /** A frase do servidor sobre a hora de pessoa sem valor (0095); nula quando não há. */
    readonly maoDeObraSemValor: string | null;
    readonly custoPorHora: number | null;
  } | null;
}

/** Uma O.S. desta maquina, como a oficina a ve. */
export interface OrdemDoProntuario {
  readonly id: string;
  readonly numero: number;
  readonly tipo: string;
  readonly etapa: string;
  readonly sintoma: string;
  readonly maquinaParada: boolean;
}

/** Uma tarefa preventiva desta maquina, como a fila do modulo 05 a ve. */
export interface VencimentoDoProntuario {
  readonly tarefaId: string;
  readonly descricao: string;
  readonly plano: string;
  readonly estado: string;
  readonly faltam: number | null;
  readonly gatilho: string;
  readonly previstaPara: string | null;
}

/**
 * Uma secao que vem de OUTRO modulo. `permitido: false` quer dizer que quem
 * esta vendo nao tem a permissao daquele modulo — e nao que nao ha dado. A
 * tela tem de dizer as duas coisas de jeitos diferentes.
 */
export type Secao<T> = ({ readonly permitido: true } & T) | { readonly permitido: false };

export interface LeituraDoHorimetro {
  readonly valor: number;
  readonly lidaEm: Date | null;
  readonly origem: string;
  /** O medidor que a maquina TEM: caminhao le odometro, e a tela escreve km. */
  readonly tipoMedidor: TipoDeMedidor;
}

export interface HorasDoProntuario {
  readonly ultimos30Dias: {
    readonly apontamentos: number;
    readonly motor: number;
    readonly producao: number;
    readonly espera: number;
    readonly deslocamento: number;
    readonly manutencao: number;
  };
  /** Quem operou nos últimos 30 dias, pela hora de motor. `nome` nulo é o apontamento sem operador. */
  readonly operadores: readonly OperadorDoProntuario[];
}

export interface OperadorDoProntuario {
  readonly nome: string | null;
  readonly apontamentos: number;
  readonly horasMotor: number;
  readonly ultimoDia: string | null;
}

export interface AbastecimentosDoProntuario {
  readonly ultimos30DiasPorProduto: readonly {
    readonly produto: string;
    readonly litros: number;
    readonly abastecimentos: number;
  }[];
  readonly ultimo: Date | null;
  /** Decisão 20: a média real, ponderada, dos últimos 90 dias. Nulo quando não há intervalo confiável. */
  readonly consumoEfetivo: { readonly valor: number; readonly intervalos: number; readonly dias: number } | null;
}

export interface DocumentoDoProntuario {
  readonly documentoId: string;
  readonly titulo: string;
  readonly origem: string;
  readonly situacao: string;
  readonly temTexto: boolean;
  readonly alcancaPor: string;
}

/**
 * A situação do documento técnico escrita para quem lê — a ficha da mesa e a do
 * celular. O celular escrevia o código do banco ("validado") até 07/10/2026.
 */
export const SITUACAO_DO_DOCUMENTO: Readonly<Record<string, string>> = {
  pendente: 'Aguardando validação',
  validado: 'Validado',
};

function numero(valor: unknown): number {
  return paraNumero(valor) ?? 0;
}

/**
 * De onde veio a leitura, escrito para quem lê — a mesa e o celular. Com a
 * telemetria (0103), "este número veio de onde" é a primeira pergunta da ficha.
 */
export const ORIGEM_DA_LEITURA: Readonly<Record<string, string>> = {
  abastecimento: 'Abastecimento',
  apontamento: 'Apontamento',
  manutencao: 'Manutenção',
  ajuste: 'Ajuste',
  campo: 'Leitura de campo',
  telemetria: 'Telemetria',
};

export function lerHorimetro(linha: Record<string, unknown> | null): LeituraDoHorimetro | null {
  if (!linha) return null;
  return {
    valor: numero(linha['valor']),
    lidaEm: paraInstante(linha['lidaEm']),
    origem: String(linha['origem'] ?? ''),
    tipoMedidor: linha['tipoMedidor'] === 'odometro' ? 'odometro' : 'horimetro',
  };
}

export function lerHoras(linha: Record<string, unknown>): Secao<HorasDoProntuario> {
  if (linha['permitido'] !== true) return { permitido: false };
  const soma = (linha['ultimos30Dias'] ?? {}) as Record<string, unknown>;
  return {
    permitido: true,
    ultimos30Dias: {
      apontamentos: numero(soma['apontamentos']),
      motor: numero(soma['motor']),
      producao: numero(soma['producao']),
      espera: numero(soma['espera']),
      deslocamento: numero(soma['deslocamento']),
      manutencao: numero(soma['manutencao']),
    },
    operadores: ((linha['operadores'] ?? []) as Record<string, unknown>[]).map((o) => ({
      nome: o['semOperador'] === true || o['operador'] == null ? null : String(o['operador']),
      apontamentos: numero(o['apontamentos']),
      horasMotor: numero(o['horasMotor']),
      ultimoDia: o['ultimoDia'] == null ? null : String(o['ultimoDia']).slice(0, 10),
    })),
  };
}

export function lerAbastecimentos(linha: Record<string, unknown>): Secao<AbastecimentosDoProntuario> {
  if (linha['permitido'] !== true) return { permitido: false };
  const porProduto = (linha['ultimos30DiasPorProduto'] ?? []) as Record<string, unknown>[];
  const recentes = (linha['recentes'] ?? []) as Record<string, unknown>[];
  return {
    permitido: true,
    ultimos30DiasPorProduto: porProduto.map((p) => ({
      produto: String(p['produto'] ?? ''),
      litros: numero(p['litros']),
      abastecimentos: numero(p['abastecimentos']),
    })),
    ultimo: paraInstante(recentes[0]?.['ocorridoEm']),
    consumoEfetivo: lerConsumoEfetivo(linha['consumoEfetivo']),
  };
}

function lerConsumoEfetivo(bruto: unknown): AbastecimentosDoProntuario['consumoEfetivo'] {
  if (bruto === null || typeof bruto !== 'object') return null;
  const c = bruto as Record<string, unknown>;
  const valor = paraNumero(c['valor']);
  if (valor === null) return null;
  return { valor, intervalos: numero(c['intervalos']), dias: numero(c['dias']) };
}

export function lerManutencao(
  linha: Record<string, unknown> | undefined,
): Secao<{ readonly itens: readonly VencimentoDoProntuario[] }> {
  if (!linha || linha['permitido'] !== true) return { permitido: false };
  const itens = (linha['itens'] ?? []) as Record<string, unknown>[];
  return {
    permitido: true,
    itens: itens.map((v) => ({
      tarefaId: String(v['tarefaId'] ?? ''),
      descricao: String(v['descricao'] ?? ''),
      plano: String(v['plano'] ?? ''),
      estado: String(v['estado'] ?? ''),
      faltam: paraNumero(v['faltam']),
      gatilho: String(v['gatilho'] ?? ''),
      previstaPara: paraTexto(v['previstaPara']),
    })),
  };
}

export function lerOrdens(linha: Record<string, unknown> | undefined): Secao<OrdensDoProntuario> {
  if (!linha || linha['permitido'] !== true) return { permitido: false };
  const itens = (linha['itens'] ?? []) as Record<string, unknown>[];
  const paradas = (linha['paradasProgramadas'] ?? []) as Record<string, unknown>[];
  const pecas = (linha['pecasAplicadas'] ?? []) as Record<string, unknown>[];
  return {
    permitido: true,
    itens: itens.map((o) => ({
      id: String(o['id'] ?? ''),
      numero: numero(o['numero']),
      tipo: String(o['tipo'] ?? ''),
      etapa: String(o['etapa'] ?? ''),
      sintoma: String(o['sintoma'] ?? ''),
      maquinaParada: o['maquinaParada'] === true,
    })),
    paradasProgramadas: paradas.map((p) => ({
      ordemId: String(p['ordemId'] ?? ''),
      numero: numero(p['numero']),
      de: paraInstante(p['de']),
      ate: paraInstante(p['ate']),
      combinadaCom: paraTexto(p['combinadaCom']),
      combinadaPor: paraTexto(p['combinadaPor']),
    })),
    pecasAplicadas: pecas.map((p) => ({
      id: String(p['id'] ?? ''),
      ordemId: String(p['ordemId'] ?? ''),
      numero: numero(p['numero']),
      descricao: String(p['descricao'] ?? ''),
      quantidade: numero(p['quantidade']),
      unidade: String(p['unidade'] ?? ''),
      aplicadaEm: paraInstante(p['aplicadaEm']),
    })),
  };
}

export function lerCusto(linha: Record<string, unknown> | undefined): Secao<CustoDoProntuario> {
  if (!linha || linha['permitido'] !== true) return { permitido: false };
  const v = linha['valores'] as Record<string, unknown> | null | undefined;
  return {
    permitido: true,
    de: String(linha['de'] ?? ''),
    ate: String(linha['ate'] ?? ''),
    denominador: String(linha['denominador'] ?? ''),
    valores: v
      ? {
          combustivel: numero(v['combustivel']),
          estoque: numero(v['estoque']),
          terceiros: numero(v['terceiros']),
          total: numero(v['total']),
          semPreco: numero(v['semPreco']),
          horasMaoDeObra: numero(v['horasMaoDeObra']),
          maoDeObraSemValor: typeof v['maoDeObraSemValor'] === 'string' ? v['maoDeObraSemValor'] : null,
          custoPorHora: paraNumero(v['custoPorHora']),
        }
      : null,
  };
}

export function lerCustoDoMes(linha: Record<string, unknown> | undefined): Secao<CustoDoMes> {
  if (!linha || linha['permitido'] !== true) return { permitido: false };
  const v = linha['valores'] as Record<string, unknown> | null | undefined;
  return {
    permitido: true,
    competencia: String(linha['competencia'] ?? ''),
    de: String(linha['de'] ?? ''),
    ate: String(linha['ate'] ?? ''),
    denominador: String(linha['denominador'] ?? ''),
    valores: v
      ? {
          combustivel: numero(v['combustivel']),
          estoque: numero(v['estoque']),
          terceiros: numero(v['terceiros']),
          outros: numero(v['outros']),
          maoDeObra: numero(v['maoDeObra']),
          total: numero(v['total']),
          semPreco: numero(v['semPreco']),
          maoDeObraSemValor: typeof v['maoDeObraSemValor'] === 'string' ? v['maoDeObraSemValor'] : null,
          custoPorHora: paraNumero(v['custoPorHora']),
          depreciacao: numero(v['depreciacao']),
          semDepreciacao: v['semDepreciacao'] === true,
          indiretoRateado: numero(v['indiretoRateado']),
          custoTotal: v['custoTotal'] === undefined ? numero(v['total']) : numero(v['custoTotal']),
          custoTotalPorHora: v['custoTotalPorHora'] === undefined ? paraNumero(v['custoPorHora']) : paraNumero(v['custoTotalPorHora']),
        }
      : null,
    receita: paraNumero(linha['receita']),
    margem: paraNumero(linha['margem']),
    semMargemPorque: paraTexto(linha['semMargemPorque']),
    ausencias: Array.isArray(linha['ausencias']) ? (linha['ausencias'] as unknown[]).filter((a): a is string => typeof a === 'string') : [],
    completo: linha['completo'] === true,
  };
}

export function lerDocumentos(
  linha: Record<string, unknown>,
): Secao<{ readonly itens: readonly DocumentoDoProntuario[] }> {
  if (linha['permitido'] !== true) return { permitido: false };
  const itens = (linha['itens'] ?? []) as Record<string, unknown>[];
  return {
    permitido: true,
    itens: itens.map((d) => ({
      documentoId: String(d['documentoId'] ?? ''),
      titulo: String(d['titulo'] ?? ''),
      origem: String(d['origem'] ?? ''),
      situacao: String(d['situacao'] ?? ''),
      temTexto: d['temTexto'] === true,
      alcancaPor: String(d['alcancaPor'] ?? ''),
    })),
  };
}

/** Um item de cadastro que vira opcao de campo de selecao. */
export interface Opcao {
  readonly id: string;
  readonly rotulo: string;
}

/**
 * A obra COM O CLIENTE DELA (26/10/2026).
 *
 * O dono: *"na obra ja tem o cliente e ao selecionar obra nao preencheu,
 * tive que fazer manual"*. O servidor sempre devolveu `cliente_id` em
 * `/equipamentos/apoio/cadastros`; era a tela que o jogava fora ao virar
 * `Opcao`, e o formulario de alocacao nao tinha como saber de quem era a
 * obra. Digitar de novo o que o cadastro ja sabe e como um cadastro de obra
 * e um cadastro de alocacao passam a discordar sobre o mesmo cliente.
 */
export interface OpcaoDeObra extends Opcao {
  readonly clienteId: string | null;
}

export interface CadastrosDeApoio {
  readonly obras: readonly OpcaoDeObra[];
  readonly clientes: readonly Opcao[];
  readonly centrosDeCusto: readonly Opcao[];
  /** O contrato leva o cliente dele (31/10/2026): escolher o contrato preenche o cliente e, com uma obra so desse cliente, a obra. */
  readonly contratos: readonly OpcaoDeObra[];
  readonly filiais: readonly Opcao[];
  readonly configuracoes: readonly OpcaoDeConfiguracao[];
}

/** A configuracao nos cadastros de apoio: o tipo e o medidor do modelo dao o icone da opcao. */
export interface OpcaoDeConfiguracao extends Opcao {
  readonly tipo: string | null;
  readonly tipoMedidor: string | null;
  /**
   * O que a máquina HERDA da configuração e o cadastro mostra em etiqueta
   * (pente fino de 31/10/2026): a capacidade do tanque e o motor de fábrica.
   * E o fabricante e o modelo, que sugerem a descrição.
   */
  readonly capacidadeTanqueL?: number | null;
  readonly motorModelo?: string | null;
  readonly fabricante?: string | null;
  readonly modelo?: string | null;
}

/** Fabricante, modelo e configuracao — a hierarquia da ferramenta 1. */
export interface Fabricante {
  readonly id: string;
  readonly nome: string;
  readonly ativo: boolean;
}

export interface Modelo extends Fabricante {
  readonly tipo: string;
  readonly fabricante: string | null;
  readonly fabricanteId: string;
}

export interface Configuracao extends Fabricante {
  readonly modeloId: string;
  readonly modelo: string | null;
  readonly fabricante: string | null;
  readonly tipo: string | null;
  readonly tipoMedidor: TipoDeMedidor;
  readonly capacidadeTanqueL: number | null;
  readonly consumoMinimo: number | null;
  readonly consumoMaximo: number | null;
  readonly potenciaKw: number | null;
}
